import React from 'react';
import { useGlobalError } from '../../contexts/global-error.context';
import { setGlobalErrorHandler } from '../../utils/api-error-handler';

export interface WithGlobalErrorProps {
    globalError?: {
        showError: (message: string, title?: string, variant?: 'critical' | 'warning' | 'info') => void;
        showSuccess: (message: string, title?: string) => void;
        showInfo: (message: string, title?: string) => void;
        showWarning: (message: string, title?: string) => void;
        clearAllNotifications: () => void;
    };
}

// HOC to inject global error handling into components
export const withGlobalError = <P extends object>(
    WrappedComponent: React.ComponentType<P>
): React.ComponentType<P & WithGlobalErrorProps> => {
    const WithGlobalErrorComponent = (props: P & WithGlobalErrorProps) => {
        const globalError = useGlobalError();
        
        // Initialize global error handler for API interceptors
        React.useEffect(() => {
            setGlobalErrorHandler({
                showError: globalError.showError,
                showSuccess: globalError.showSuccess
            });
        }, [globalError]);

        return (
            <WrappedComponent 
                {...props} 
                globalError={globalError}
            />
        );
    };

    WithGlobalErrorComponent.displayName = `withGlobalError(${WrappedComponent.displayName || WrappedComponent.name})`;
    
    return WithGlobalErrorComponent;
};

// Component to initialize global error handler
export const GlobalErrorInitializer: React.FC = () => {
    const globalError = useGlobalError();
    
    React.useEffect(() => {
        setGlobalErrorHandler({
            showError: globalError.showError,
            showSuccess: globalError.showSuccess
        });
    }, [globalError]);

    return null;
};
